import { Button, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  RequestForInvoicingStatusEnum,
  useCreateProformaInvoiceMutation,
  useCreateRequestForInvoicingMutation,
  useUpdateProformaInvoiceMutation,
  useUpdateRequestForInvoicingMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React from "react";
import { useInvoiceStore } from "../../store";
import { SubmitForInvoiceButton } from "./SubmitForInvoiceButton";

export const CreateProformaButton = observer(function CreateProformaButton() {
  const store = useInvoiceStore();
  const router = useRouter();
  const appStore = useStore();

  const [createProformaInvoiceMutation, createProformaInvoiceRequest] =
    useCreateProformaInvoiceMutation({
      onCompleted({ createOutgoingProformaInvoice }) {
        if (!createOutgoingProformaInvoice) return;

        appStore.UIStore.toast({
          title: t`Proforma created`,
          status: "success",
        });

        router.push({
          pathname: "/invoices/proforma-invoices/detail",
          query: {
            id: createOutgoingProformaInvoice.id,
          },
        });
      },
      ...onError(),
    });

  const [
    createRequestForInvoiceAndProformaInvoiceMutation,
    createRequestForInvoiceAndProformaInvoiceRequest,
  ] = useCreateRequestForInvoicingMutation({
    onCompleted(data) {
      if (!data.createRequestForInvoicing.workspace_bank_account_id) {
        appStore.UIStore.toast({
          title: t`Proforma invoice wasn't created because bank account isn't selected.`,
          status: "warning",
        });
        router.push({
          pathname: "/invoices/proforma-invoices",
        });
        return;
      }
      if (
        !store.form.invoice_no.value ||
        !store.form.invoice_order_sequence_id.value
      ) {
        appStore.UIStore.toast({
          status: "error",
        });
        captureEvent({
          message:
            "Proforma invoice wasn't created because invoice no or invoice order sequence id isn't selected.",
          extra: {
            invoice_no: store.form.invoice_no.value,
            invoice_order_sequence_id:
              store.form.invoice_order_sequence_id.value,
          },
        });
        return;
      }
      createProformaInvoiceMutation({
        variables: {
          request_for_invoicing_id: data.createRequestForInvoicing.id,
          skip_date_validations: store.skip_date_validations,
          invoice_no: store.form.invoice_no.value,
          invoice_order_sequence_id: store.form.invoice_order_sequence_id.value,
        },
      });
    },
    ...onError(),
  });

  const [
    updateRequestForInvoiceAndProformaInvoiceMutation,
    updateRequestForInvoiceAndProformaInvoiceRequest,
  ] = useUpdateRequestForInvoicingMutation({
    onCompleted(data) {
      if (!data.updateRequestForInvoicing.workspace_bank_account_id) {
        appStore.UIStore.toast({
          title: t`Proforma invoice wasn't created because bank account isn't selected.`,
          status: "warning",
        });
        router.push({
          pathname: "/invoices/proforma-invoices",
        });
        return;
      }
      if (
        !store.form.invoice_no.value ||
        !store.form.invoice_order_sequence_id.value
      ) {
        appStore.UIStore.toast({
          status: "error",
        });
        captureEvent({
          message:
            "Proforma invoice wasn't created because invoice no or invoice order sequence id isn't selected.",
          extra: {
            invoice_no: store.form.invoice_no.value,
            invoice_order_sequence_id:
              store.form.invoice_order_sequence_id.value,
          },
        });
        return;
      }
      createProformaInvoiceMutation({
        variables: {
          request_for_invoicing_id: data.updateRequestForInvoicing.id,
          skip_date_validations: store.skip_date_validations,
          invoice_no: store.form.invoice_no.value,
          invoice_order_sequence_id: store.form.invoice_order_sequence_id.value,
        },
      });
    },
    ...onError(),
  });

  const onCreateProformaInvoice = async () => {
    const { hasError } = await store.validateAllSteps();
    if (hasError) return;

    if (store.invoice_id) {
      updateRequestForInvoiceAndProformaInvoiceMutation({
        variables: {
          input: store.updateRequestForInvoiceInput(
            RequestForInvoicingStatusEnum.Submitted,
          ),
          skip_date_validations: store.skip_date_validations,
        },
      });
    } else {
      createRequestForInvoiceAndProformaInvoiceMutation({
        variables: {
          input: store.createRequestForInvoiceInput(
            RequestForInvoicingStatusEnum.Submitted,
          ),
          skip_date_validations: store.skip_date_validations,
        },
      });
    }
  };

  const [updateProformaInvoiceMutation, updateProformaInvoiceRequest] =
    useUpdateProformaInvoiceMutation({
      onCompleted: ({ updateOutgoingProformaInvoice }) => {
        if (!updateOutgoingProformaInvoice) return;

        appStore.UIStore.toast({
          title: t`Proforma updated`,
          status: "success",
        });

        router.push({
          pathname: "/invoices/proforma-invoices/detail",
          query: { id: updateOutgoingProformaInvoice.id },
        });
      },
      ...onError(),
    });

  const onUpdateProformaInvoice = async () => {
    const { hasError } = await store.validateAllSteps();
    if (hasError) return;

    updateProformaInvoiceMutation({
      variables: {
        input: store.updateOutgoingProformaInvoiceInput(),
        skip_date_validations: store.skip_date_validations,
      },
    });
  };

  store.setInvoiceMutationLoading(
    createProformaInvoiceRequest.loading ||
      createRequestForInvoiceAndProformaInvoiceRequest.loading ||
      updateRequestForInvoiceAndProformaInvoiceRequest.loading ||
      updateProformaInvoiceRequest.loading,
  );

  return (
    <React.Fragment>
      {store.isRFI ? (
        store.isFinancier ? (
          <Tooltip
            isDisabled={!store.isLocked}
            label={store.isLocked ? t`Proforma invoice is locked` : undefined}
          >
            <Button
              px={{
                base: "2",
                md: "4",
              }}
              isDisabled={!!store.projectOverdue || store.isLocked}
              isLoading={
                store.convertAmountFetcher.isLoading.value ||
                store.exchangeRateFetcher.isLoading.value
              }
              onClick={onCreateProformaInvoice}
            >
              <Trans>Create proforma</Trans>
            </Button>
          </Tooltip>
        ) : (
          <SubmitForInvoiceButton />
        )
      ) : (
        <Tooltip
          label={
            !store.isFinancier
              ? t`You don’t have permission to update the proforma`
              : store.isLocked
                ? t`Proforma invoice is locked`
                : undefined
          }
        >
          <Button
            px={{ base: "2", md: "4" }}
            isDisabled={
              !!store.projectOverdue || !store.isFinancier || store.isLocked
            }
            isLoading={
              store.convertAmountFetcher.isLoading.value ||
              store.exchangeRateFetcher.isLoading.value
            }
            onClick={onUpdateProformaInvoice}
          >
            <Trans>Update proforma</Trans>
          </Button>
        </Tooltip>
      )}
    </React.Fragment>
  );
});
